import React, { useMemo, useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { safeLocalStorage } from '../../../lib/safeBrowserStorage';
import { useTranslation } from 'react-i18next';
import { getFailInfo, registerFail, resetFails, computeBackoffMs } from '../../security/rateLimiter';

export function LoginForm({ setView, setError, setMessage, close, setLoading }) {
  const [email,setEmail] = useState('');
  const [pwd,setPwd] = useState('');
  const [showPwd,setShowPwd] = useState(false);
  const [remember,setRemember] = useState(true);
  const { t } = useTranslation();

  const failInfo = useMemo(()=> getFailInfo(email), [email]);
  const now = Date.now();
  const cooldownActive = failInfo.cooldownUntil && failInfo.cooldownUntil > now;
  const backoffMs = computeBackoffMs(failInfo);
  const [captchaToken, setCaptchaToken] = useState('');
  const needsCaptcha = failInfo.count >=3 && !cooldownActive;
  const [showCaptchaOverlay, setShowCaptchaOverlay] = useState(false);

  // Mostrar overlay solamente cuando recién pasamos el umbral o mientras no se haya validado
  useEffect(()=>{
    if (needsCaptcha && !captchaToken) setShowCaptchaOverlay(true);
    if (!needsCaptcha) { setShowCaptchaOverlay(false); setCaptchaToken(''); }
  }, [needsCaptcha, captchaToken]);

  const onSubmit = async (e)=>{
    e.preventDefault();
    setError(''); setMessage(''); setLoading(true);
    try {
      if (cooldownActive) {
        const mins = Math.ceil((failInfo.cooldownUntil - now)/60000);
        throw new Error(t('auth.errors.cooldown','Demasiados intentos. Inténtalo de nuevo en {{mins}} min',{ mins }));
      }
      if (needsCaptcha && !captchaToken) {
        throw new Error(t('auth.captcha.required','Completa la verificación para continuar'));
      }
      if (backoffMs > 0) {
        await new Promise(r=>setTimeout(r, backoffMs));
      }
      const normalizedEmail = String(email || '').trim().toLowerCase();
      const normalizedPassword = String(pwd || '');
      safeLocalStorage.setItem('auth.remember', remember ? '1':'0');

      const { error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: normalizedPassword,
      });
      if(error) throw error;
      resetFails(normalizedEmail);
      setMessage(t('auth.logged_in','Signed in'));
      // El cierre/navegación será gestionado por el efecto en AuthModal cuando detecte user.
    } catch(err){
      // Registrar fallo para backoff/cooldown posteriores
      try { registerFail(String(email || '').trim().toLowerCase()); } catch {}
      setError(err.message || t('auth.errors.login_failed','Could not sign in'));
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1">{t('auth.email','Email')}</label>
  <input autoFocus type="email" value={email} onChange={e=>setEmail(e.target.value)} required className="input-primary w-full" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">{t('auth.password','Password')}</label>
        <div className="relative">
          <input type={showPwd?'text':'password'} value={pwd} onChange={e=>setPwd(e.target.value)} required className="input-primary w-full pr-10" />
          <button type="button" onClick={()=>setShowPwd(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">{showPwd? '🙈':'👁️'}</button>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <label className="flex items-center gap-2 cursor-pointer select-none"><input type="checkbox" checked={remember} onChange={e=>setRemember(e.target.checked)} /><span>{t('auth.remember_me','Remember me')}</span></label>
        <button type="button" onClick={()=>setView('reset')} className="text-gray-400 hover:text-primary">{t('auth.forgot','Forgot your password?')}</button>
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={close} className="btn-secondary">{t('common.cancel','Cancel')}</button>
        <button type="submit" className="btn-primary" disabled={cooldownActive || (needsCaptcha && !captchaToken)} title={cooldownActive? t('auth.errors.cooldown_short','Demasiados intentos. Espera un momento.'): (needsCaptcha && !captchaToken ? t('auth.captcha.required.short','Completa la verificación'): undefined)}>{t('auth.continue','Continue')}</button>
      </div>
      {/* Overlay flotante para CAPTCHA */}
      {showCaptchaOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-lg z-10">
          <div className="bg-dark-light border border-gray-700 rounded-xl p-5 w-full max-w-xs shadow-xl relative">
            <div className="text-sm font-semibold text-white mb-2">{t('auth.captcha.title','Verificación adicional')}</div>
            <p className="text-xs text-gray-300 mb-4 leading-relaxed">{t('auth.captcha.helper','Confirma que eres humano para continuar. Esto protege tu cuenta.')}</p>
            {/* Placeholder para widget real (Turnstile / hCaptcha). Reemplazar este bloque cuando se integren las keys. */}
            <div className="mb-4" id="captcha-widget">
              {!captchaToken ? (
                <button type="button" onClick={()=>setCaptchaToken('dev-token')} className="w-full py-2 rounded-lg bg-primary hover:brightness-110 text-sm font-medium text-white transition-all">{t('auth.captcha.dev_button','No soy un robot (demo)')}</button>
              ) : (
                <div className="text-green-400 text-xs flex items-center gap-2">✔ {t('auth.captcha.verified','Verificado')}</div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={()=>{ setCaptchaToken(''); setShowCaptchaOverlay(false); }} className="text-xs text-gray-400 hover:text-gray-200 transition-colors">
                {t('common.cancel','Cancelar')}
              </button>
              {captchaToken ? (
                <button type="button" onClick={()=> setShowCaptchaOverlay(false)} className="btn-primary px-4 py-1.5 text-xs">{t('common.close','Cerrar')}</button>
              ) : (
                <button type="button" onClick={()=>{ /* mantener overlay hasta verificación */ }} disabled className="btn-primary px-4 py-1.5 text-xs opacity-40 cursor-not-allowed">{t('auth.continue','Continue')}</button>
              )}
            </div>
            <div className="mt-3 text-[10px] text-gray-500 leading-snug">{t('auth.captcha.privacy','Tu verificación se utiliza sólo para mitigar abusos.')} </div>
          </div>
        </div>
      )}
    </form>
  );
}
